import {
  FullscreenExitOutlined,
  FullscreenOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  UnlockOutlined,
  EditOutlined
} from '@ant-design/icons'
import { Alert, Space, Tag, message } from 'antd'
import Button from 'Components/Button'
import { useEffect, useState } from 'react'
import apiClient from 'Utils/apiClient'
import DocumentRetentionModal from '../../Components/DocumentRetention/DocumentRetentionModal'
import DocumentComments from '../../Components/DocumentComments/DocumentComments'
import TagPicker from '../../Components/TagPicker/TagPicker'
import './Drive.scss'
import VersionHistory from '../../Components/VersionHistory/VersionHistory'
import OnlyOfficeEditorModal from '../../Components/OnlyOffice/OnlyOfficeEditorModal'
import RenderDocumentContent from './RenderDocumentContent'

const resolveAccessId = (doc) => doc?.id || doc?._id || null
const resolveDocumentId = (doc) => doc?.documentId || doc?.id || doc?._id || null

const DrivePreview = ({ previewData, setDrawerFullView, drawerFullView }) => {
  const [documentMeta, setDocumentMeta] = useState(() => previewData || {})
  const [retentionOpen, setRetentionOpen] = useState(false)
  const [editConfig, setEditConfig] = useState(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorLoading, setEditorLoading] = useState(false)

  // Preview stream is keyed by DocumentAccess id; retention/editor use DocumentManagement id.
  const accessId = resolveAccessId(previewData)
  const documentId = resolveDocumentId(previewData)

  useEffect(() => {
    setDocumentMeta(previewData || {})
    setEditConfig(null)
  }, [previewData?.id, previewData?.documentId])

  const onExpand = () => {
    setDrawerFullView(!drawerFullView)
  }

  const onCheckout = () => {
    if (!documentId) {
      return
    }

    apiClient.post(`filestructure/${documentId}/checkout`).then(({ data }) => {
      if (data?.result) {
        setDocumentMeta((prev) => ({ ...prev, ...data.result }))
      }
    })
  }

  const onCheckin = () => {
    if (!documentId) {
      return
    }

    apiClient.post(`filestructure/${documentId}/checkin`).then(({ data }) => {
      if (data?.result) {
        setDocumentMeta((prev) => ({ ...prev, ...data.result }))
      }
    })
  }

  const onOpenEditor = async () => {
    if (!documentId) {
      message.error('Document id missing')
      return
    }

    if (editConfig) {
      setEditorOpen(true)
      return
    }

    setEditorLoading(true)

    try {
      const response = await apiClient.get(`documents/${documentId}/edit-config`, {
        validateStatus: () => true
      })
      const { data, status } = response

      if (status === 501 || !data?.result?.documentServerUrl || !data?.result?.config) {
        message.warning(
          data?.message ||
            'OnlyOffice Document Server is not configured. LibreOffice is used for previews only — set ONLYOFFICE_URL to enable Edit in OnlyOffice.'
        )
        return
      }

      if (status >= 400 || !data?.success) {
        message.error(data?.message || 'Could not open editor')
        return
      }

      setEditConfig(data.result)
      setEditorOpen(true)
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Could not open OnlyOffice')
    } finally {
      setEditorLoading(false)
    }
  }

  return (
    <div className="drive-preview-panel">
      <div className="drive-drawer-actions">
        <Space wrap>
          {(documentMeta?.legalHold || documentMeta?.retentionDays > 0) && (
            <Tag color={documentMeta.legalHold ? 'red' : 'blue'}>
              {documentMeta.legalHold ? 'Legal hold' : `${documentMeta.retentionDays}d retention`}
            </Tag>
          )}
          {documentId && (
            <Button onClick={() => setRetentionOpen(true)}>
              <SafetyCertificateOutlined /> Retention
            </Button>
          )}
          {documentId && previewData?.type === 'File' && (
            <>
              {documentMeta?.checkedOutBy ? (
                <Button onClick={onCheckin}>
                  <UnlockOutlined /> Check in
                </Button>
              ) : (
                <Button onClick={onCheckout}>
                  <LockOutlined /> Check out
                </Button>
              )}
              <Button onClick={onOpenEditor} loading={editorLoading}>
                <EditOutlined /> Edit in OnlyOffice
              </Button>
            </>
          )}
          <Button onClick={onExpand}>
            {drawerFullView ? (
              <>
                <FullscreenExitOutlined /> Collapse
              </>
            ) : (
              <>
                <FullscreenOutlined /> Expand
              </>
            )}
          </Button>
        </Space>
      </div>

      <div className="drive-preview">
        <RenderDocumentContent
          documentId={accessId}
          fileType={documentMeta?.fileType || previewData?.fileType}
          fileName={documentMeta?.name || previewData?.name || previewData?.currentName}
          fileSize={documentMeta?.size || previewData?.size}
        />

        {documentId && previewData?.type === 'File' && (
          <div className="drive-preview__meta">
            <h4>Tags</h4>
            <TagPicker documentId={documentId} />
            <h4>Comments</h4>
            <DocumentComments documentId={documentId} />
            <h4>Version history</h4>
            <VersionHistory documentId={documentId} />
          </div>
        )}
      </div>

      <Alert
        className="drive-confidential-banner"
        type="warning"
        showIcon
        icon={<SafetyCertificateOutlined />}
        message="This document is confidential"
        description="Do not share, download, or distribute outside authorized recipients without approval."
        banner
      />

      <DocumentRetentionModal
        documentId={documentId}
        open={retentionOpen}
        onClose={() => setRetentionOpen(false)}
      />
      <OnlyOfficeEditorModal open={editorOpen} onClose={() => setEditorOpen(false)} editConfig={editConfig} />
    </div>
  )
}

export default DrivePreview
